import Vue from "vue"
import Vuex from "vuex"
import VueRouter from "vue-router"
import EventEmitter from "./EventEmitter"

/**
 * Точка входа в приложение
 */
export default class Application extends EventEmitter {
	/**
	 * Инициализировать приложение
	 * @param {Object} component
	 * @param {Object} assets
	 */
	constructor(component, { config, context, store, routes, plugins }) {
		super()

		if (!component) {
			throw "Application: Необходимо передать корневой компонент в приложение"
		}

		this.component = component
		this.config = config || {}
		this.context = context || {}
		this.store = store || {}
		this.routes = routes || []
		this.plugins = plugins || []

		this.initVuex()
		this.initRouter()
		this.initPlugins()
		this.initVue()
	}

	/**
	 * Инициализировать Vue
	 */
	initVue() {
		Vue.config.productionTip = !this.config.debug

		// Расшарить контекст во Vue компонентах
		Vue.use({
			install: Vue => {
				Vue.$context = this.context
				Vue.prototype.$context = this.context
			}
		})

		this.vue = new Vue({
			router: this.router,
			store: this.vuex,
			render: h => h(this.component)
		})
	}

	/**
	 * Инициализировать роутер
	 */
	initRouter() {
		const config = this.config.router || {}
		const baseUrl = config.baseUrl || "/"
		const baseMeta = config.meta || {}
		const defaultTitle = baseMeta.title || "App"
		const defaultGuard = baseMeta.guard || (({ next }) => next())
		const routes = this.getAsset(this.routes)

		Vue.use(VueRouter)

		// Роутер приложения
		this.router = new VueRouter({
			mode: "history",
			base: baseUrl,
			routes
		})

		// Перед каждым переходом по маршруту
		this.router.beforeEach((to, from, next) => {
			const meta = to.meta || {}
			const guard = meta.guard || defaultGuard || (() => next())

			// Пройти охрану маршрута
			guard.call(null, { to, from, next }, this.context)
		})

		// После каждого перехода по маршруту
		this.router.afterEach(to => {
			const meta = to.meta || {}

			// Кстановить title страницы
			document.title = meta.title || defaultTitle
		})
	}

	/**
	 * Инициализировать хранилище состояний Vuex
	 */
	initVuex() {
		const store = this.getAsset(this.store)

		Vue.use(Vuex)

		// Хранилище состояний приложения
		this.vuex = new Vuex.Store(store)
	}

	/**
	 * Инициализировать плагины
	 */
	initPlugins() {
		const plugins = this.getAsset(this.plugins)

		plugins.forEach(plugin => {
			if (plugin instanceof Object && plugin.install) {
				Vue.use(plugin)
			}
		})
	}

	/**
	 * Монтировать приложение
	 * @param {String} selector
	 */
	async mount(selector) {
		// Ждать отработку события перед монтированием
		await this.emit("beforeMount", this)

		// Монтировать Vue
		this.vue.$mount(selector)
	}

	/**
	 * Получить актив приложения
	 * @param {Object|Array|Function} asset
	 * @return {Object|Array}
	 */
	getAsset(asset) {
		return asset instanceof Function ? asset.call(null, this.context) : asset
	}
}
